import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  const token = searchParams.get('token');
  const emailParam = searchParams.get('email');

  useEffect(() => {
    if (!token || !emailParam) {
      setStatus('error');
      setMessage('Lien de vérification invalide.');
      return;
    }

    setEmail(emailParam);
    verifyEmail();
  }, [token, emailParam]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, {
        email: emailParam,
        token: token
      });

      if (response.data.ok) {
        setStatus('success');
        setMessage(response.data.message);
        
        // Auto-login with returned token
        if (response.data.access_token) {
          console.log('[VERIFY] Token received, storing...');
          localStorage.setItem('access_token', response.data.access_token);
          setToken(response.data.access_token);
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
            setUser(response.data.user);
          }
          
          toast.success('Email vérifié ! Redirection...');
          setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur de vérification';
      setStatus(detail.includes('expiré') ? 'expired' : 'error');
      setMessage(detail);
    }
  };

  const handleResendEmail = async () => {
    if (!email) return;
    
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success('Email de vérification envoyé !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  return (
    <div 
      className="min-h-screen bg-[#050505] flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.95), rgba(5,5,5,0.98)), url('https://images.unsplash.com/photo-1578300253266-dedd2cd40912?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <img 
          src="/assets/trackvibes-logo.png" 
          alt="TrackVibes" 
          className="h-16 w-auto mx-auto mb-6"
        />

        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-neon-cyan mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-xl text-white mb-2">Vérification en cours...</h2>
            <p className="text-gray-400">Patiente quelques secondes</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h2 className="font-heading text-xl text-white mb-2">Email vérifié !</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <p className="text-neon-cyan text-sm">Redirection vers le dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="font-heading text-xl text-white mb-2">Erreur de vérification</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <Link to="/login">
              <Button className="bg-neon-cyan text-black font-bold rounded-full px-8">
                Retour à la connexion
              </Button>
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-12 h-12 text-amber-500" />
            </div>
            <h2 className="font-heading text-xl text-white mb-2">Lien expiré</h2>
            <p className="text-gray-400 mb-6">{message}</p>
            <Button 
              onClick={handleResendEmail}
              className="bg-neon-cyan text-black font-bold rounded-full px-8 mb-4"
            >
              Renvoyer l'email de vérification
            </Button>
            <p className="text-gray-500 text-sm">
              ou{' '}
              <Link to="/login" className="text-neon-cyan hover:underline">
                retour à la connexion
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
